import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Coins, Landmark, ArrowRight, ChevronDown, Gem, Cake, Wallet, Calculator, Laptop, Smartphone } from "lucide-react";

type SubLink = { to: string; label: string };

type BilingualOption = {
  to: string;
  flag: string;
  english: string;
  native: string;
  nativeLang: "ur" | "hi" | "tr";
  nativeDir: "rtl" | "ltr";
};

type DirectCard = {
  id: string;
  kind: "link";
  title: string;
  subtext: string;
  primaryTo: string;
  primaryLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  ring: string;
  glow: string;
};

type BilingualCard = {
  id: string;
  kind: "bilingual";
  title: string;
  subtext: string;
  primaryLabel: string;
  options: BilingualOption[];
  icon: React.ComponentType<{ className?: string }>;
  ring: string;
  glow: string;
};

type AccordionGroup = { region: string; flag: string; links: SubLink[] };

type AccordionCard = {
  id: string;
  kind: "accordion";
  title: string;
  subtext: string;
  primaryLabel: string;
  groups: AccordionGroup[];
  icon: React.ComponentType<{ className?: string }>;
  ring: string;
  glow: string;
};

type Card = DirectCard | BilingualCard | AccordionCard;

const CARDS: Card[] = [
  {
    id: "gold",
    kind: "bilingual",
    title: "Check Gold Price",
    subtext: "Live 24K, 22K & 21K gold rates — per tola & per 10 gram.",
    primaryLabel: "Choose Country",
    options: [
      {
        to: "/pakistan",
        flag: "🇵🇰",
        english: "Gold Price in Pakistan",
        native: "پاکستان میں سونے کی قیمت",
        nativeLang: "ur",
        nativeDir: "rtl",
      },
      {
        to: "/india",
        flag: "🇮🇳",
        english: "Gold Price in India",
        native: "भारत में सोने का भाव",
        nativeLang: "hi",
        nativeDir: "ltr",
      },
      {
        to: "/uae",
        flag: "🇦🇪",
        english: "Gold Price in UAE",
        native: "أسعار الذهب في الإمارات",
        nativeLang: "ur",
        nativeDir: "rtl",
      },
      {
        to: "/ar/gold-rate-saudi-arabia",
        flag: "🇸🇦",
        english: "Gold Price in Saudi Arabia",
        native: "أسعار الذهب في السعودية",
        nativeLang: "ur",
        nativeDir: "rtl",
      },
      {
        to: "/tr/canli-gram-altin-fiyatlari",
        flag: "🇹🇷",
        english: "Gold Price in Turkey",
        native: "Canlı Gram Altın Fiyatları",
        nativeLang: "tr",
        nativeDir: "ltr",
      },
    ],
    icon: Coins,
    ring: "ring-yellow-500/30",
    glow: "from-yellow-500/15 via-amber-500/5 to-transparent",
  },
  {
    id: "silver",
    kind: "bilingual",
    title: "Check Silver Price",
    subtext: "Live chandi / silver rates — per tola, 10 gram & per kilogram.",
    primaryLabel: "Choose Country",
    options: [
      {
        to: "/silver-rate-pakistan",
        flag: "🇵🇰",
        english: "Silver Price in Pakistan",
        native: "پاکستان میں چاندی کی قیمت",
        nativeLang: "ur",
        nativeDir: "rtl",
      },
      {
        to: "/silver-rate-india",
        flag: "🇮🇳",
        english: "Silver Price in India",
        native: "भारत में चांदी का भाव",
        nativeLang: "hi",
        nativeDir: "ltr",
      },
    ],
    icon: Gem,
    ring: "ring-slate-400/40",
    glow: "from-slate-400/15 via-zinc-300/5 to-transparent",
  },
  {
    id: "loans",
    kind: "link",
    title: "International Debt & Loan Optimizer",
    subtext: "Calculate global bank EMIs & minimize amortization interest.",
    primaryTo: "/calculators",
    primaryLabel: "Open Calculator Hub",
    icon: Landmark,
    ring: "ring-violet-500/30",
    glow: "from-violet-500/15 via-fuchsia-500/5 to-transparent",
  },
  {
    id: "age",
    kind: "link",
    title: "Age Calculator",
    subtext:
      "Exact age in years, months, days, weeks, hours — plus milestones & retirement.",
    primaryTo: "/age-calculator",
    primaryLabel: "Open Age Calculator",
    icon: Cake,
    ring: "ring-pink-500/30",
    glow: "from-pink-500/15 via-rose-500/5 to-transparent",
  },
  {
    id: "earning-apps",
    kind: "link",
    title: "Earning Apps Review",
    subtext:
      "Honest Real / Fake verdicts on 20+ tested earning apps — payout proof & red flags.",
    primaryTo: "/earning-apps-review",
    primaryLabel: "Open Reviews",
    icon: Wallet,
    ring: "ring-sky-500/30",
    glow: "from-sky-500/15 via-cyan-500/5 to-transparent",
  },
  {
    id: "calculators-hub",
    kind: "link",
    title: "All Calculators Hub",
    subtext:
      "120+ free calculators in one directory — gold, zakat, loan EMI, BMI, pregnancy, age & 80 utility tools.",
    primaryTo: "/calculators",
    primaryLabel: "Open Calculators Hub",
    icon: Calculator,
    ring: "ring-violet-500/30",
    glow: "from-violet-500/15 via-fuchsia-500/5 to-transparent",
  },
  {
    id: "remote-jobs",
    kind: "link",
    title: "Non-Phone Remote Job Portals",
    subtext:
      "Verified daily asynchronous, chat support & data moderation opportunities with zero cold calling.",
    primaryTo: "/remote-jobs",
    primaryLabel: "Explore Remote Hub",
    icon: Laptop,
    ring: "ring-amber-500/30",
    glow: "from-amber-500/15 via-orange-500/5 to-transparent",
  },
  {
    id: "app-testing",
    kind: "link",
    title: "Verified App & Software Testing Directory",
    subtext:
      "Independent platform evaluations, screening guidelines, and verified sign-up directories for user-feedback testing.",
    primaryTo: "/app-testing",
    primaryLabel: "Explore Testing Hub",
    icon: Smartphone,
    ring: "ring-teal-500/30",
    glow: "from-teal-500/15 via-cyan-500/5 to-transparent",
  },
];

export function SiloedZonesGrid() {
  const [openId, setOpenId] = useState<string | null>(null);
  const toggle = (id: string) => setOpenId((cur) => (cur === id ? null : id));

  return (
    <section
      aria-label="DrHint global utility zones"
      className="mx-auto max-w-6xl px-4 py-12"
    >
      <header className="mb-8">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight">
          Five Independent Global Utilities
        </h2>
        <p className="mt-2 text-sm text-muted-foreground max-w-2xl">
          Each module is fully geo-neutral until you interact with it. Pick the
          tool you need — pricing, units and currency adapt to your region on
          demand.
        </p>
      </header>

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 items-stretch">
        {CARDS.map((c) => {
          const Icon = c.icon;
          const isOpen = openId === c.id;
          const expandable = c.kind !== "link";
          return (
            <article
              key={c.id}
              className={`group relative flex flex-col rounded-3xl border border-border/60 bg-card/50 p-6 ring-1 ${c.ring} backdrop-blur transition-all hover:shadow-lg`}
            >
              <div
                aria-hidden
                className={`pointer-events-none absolute inset-0 rounded-3xl bg-gradient-to-br ${c.glow} opacity-60`}
              />
              <div className="relative flex items-start gap-4">
                <div className="rounded-2xl border border-border/60 bg-background/80 p-3 shadow-sm">
                  <Icon className="h-6 w-6 text-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-lg md:text-xl font-bold leading-tight flex items-center gap-1.5">
                    {c.title}
                    {expandable && (
                      <ChevronDown
                        className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                        aria-hidden
                      />
                    )}
                  </h3>
                  <p className="mt-1.5 text-sm text-muted-foreground">{c.subtext}</p>
                </div>
              </div>

              <div className="relative mt-auto pt-6">
                {c.kind === "link" ? (
                  <Link
                    to={c.primaryTo}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-primary/10 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/20"
                  >
                    {c.primaryLabel}
                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                  </Link>
                ) : (
                  <button
                    type="button"
                    onClick={() => toggle(c.id)}
                    aria-expanded={isOpen}
                    aria-controls={`zone-panel-${c.id}`}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-primary/10 px-4 py-2 text-sm font-semibold text-primary hover:bg-primary/20"
                  >
                    {c.primaryLabel}
                    <ChevronDown
                      className={`h-4 w-4 transition-transform duration-200 ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                )}
              </div>

              {expandable && (
                <div
                  id={`zone-panel-${c.id}`}
                  className={`relative grid transition-all duration-300 ease-out ${
                    isOpen
                      ? "grid-rows-[1fr] opacity-100 mt-4"
                      : "grid-rows-[0fr] opacity-0 mt-0"
                  }`}
                >
                  <div className="overflow-hidden">
                    {c.kind === "bilingual" && (
                      <div className="grid gap-2 sm:grid-cols-2">
                        {c.options.map((o) => (
                          <Link
                            key={o.to}
                            to={o.to}
                            onClick={() => setOpenId(null)}
                            className="group/opt block rounded-2xl border border-border/60 bg-background/80 p-3 backdrop-blur hover:border-primary/50 hover:bg-primary/5 transition-colors"
                          >
                            <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                              <span className="text-base leading-none" aria-hidden>
                                {o.flag}
                              </span>
                              <span>{o.english}</span>
                            </div>
                            <div
                              lang={o.nativeLang}
                              dir={o.nativeDir}
                              className={`mt-1.5 text-sm text-muted-foreground group-hover/opt:text-foreground ${
                                o.nativeDir === "rtl" ? "text-right" : ""
                              }`}
                            >
                              {o.native}
                            </div>
                          </Link>
                        ))}
                      </div>
                    )}
                    {c.kind === "accordion" && (
                      <div className="rounded-2xl border border-border/60 bg-background/70 p-3 backdrop-blur space-y-3">
                        {c.groups.map((g) => (
                          <div key={g.region}>
                            <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1.5 px-1">
                              {g.flag} {g.region}
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                              {g.links.map((l) => (
                                <Link
                                  key={l.to}
                                  to={l.to}
                                  onClick={() => setOpenId(null)}
                                  className="rounded-lg border border-border/60 bg-card/60 px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-primary/10 hover:text-primary hover:border-primary/40 transition-colors"
                                >
                                  {l.label}
                                </Link>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </section>
  );
}